/**
 * Terminal Users registry — /api/admin/terminal-users. Mirrors admin.ts's
 * fetch wrapper + CSRF handling exactly (same cookie, same header). The
 * write verb is a single upsert keyed by `username`: POSTing an existing
 * username edits that row instead of creating a second one, so this module
 * only exposes one write function (upsertTerminalUser) for both create and
 * edit — same pattern the caller (TerminalUsers.tsx) uses for its single
 * dialog component. setTerminalUserPassword is a second, separate write: it
 * sets the operator's pacebi-managed web-terminal login password, which is
 * unrelated to the row fields above and to the operator's Pacesetter/ERP
 * login typed at the green screen.
 *
 * getPrinters/getTerminalStartup/provisionTerminal are a third, unrelated
 * concern: provisioning a register's store + printer routing (the ERP
 * startup.p row, keyed by `startdev` — a row's `pacetty`). These go through
 * paceWriteServ (see handlers_terminal.go), not the admin registry store, so
 * writes are additionally gated by the customer's writes_enabled feature
 * flag server-side (404 when off) on top of the usual CSRF + permission
 * checks.
 *
 * downloadProvisionScript is a fourth, unrelated concern: downloading a
 * generated (never executed by pacebi) OS-login provisioning script for the
 * whole registry — see handlers_paceuser.go's provision-script route.
 *
 * createOperator is a fifth, unrelated (and the most sensitive) concern:
 * minting an ERP green-screen operator login (a Progress _User authenticated
 * by SETUSERID) — completely separate from setTerminalUserPassword's pacebi
 * WEB login and from the registry row itself.
 */

export interface TerminalUser {
  username: string;
  full_name: string;
  pacetty: string;
  os_user: string;
  status: 'active' | 'inactive';
  modified_at: string;
}

export type TerminalUserInput = Omit<TerminalUser, 'modified_at'>;

export interface Printer {
  prc_num: number;
  prc_type: string;
  prc_dev: string;
  prc_form: string;
}

/** Current startup.p row for a `startdev` (a row's pacetty). `found:false`
 *  means there's no existing row yet — the provisioning form starts blank. */
export interface TerminalStartup {
  found: boolean;
  sid: number;
  /** Printer routing slots 1..11 (p1..p11), in order. */
  pr_array: number[];
  prlocal: string;
  prnorecpt: 0 | 1;
  cashbox: number;
  local_form: string;
}

export interface ProvisionTerminalInput {
  startdev: string;
  sid: number;
  /** Exactly 11 entries: printers[0]=p1 .. printers[10]=p11. */
  printers: number[];
  prlocal: string;
  prnorecpt: boolean;
  cashbox: number;
  local_form: string;
}

export interface CreateOperatorInput {
  userid: string;
  password: string;
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

async function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  if (res.status === 401) throw new Error('unauthenticated');
  if (res.status === 403) throw new Error('forbidden');
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `HTTP ${res.status}`);
  }
  // 204 has no body.
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

function csrfHeaders(): Record<string, string> {
  const csrf = readCookie('pacebi_csrf');
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) h['X-CSRF-Token'] = csrf;
  return h;
}

export function listTerminalUsers(): Promise<TerminalUser[]> {
  return fetchJSON<TerminalUser[]>('/api/admin/terminal-users');
}

/** Create-or-edit: the server upserts by `username`. */
export function upsertTerminalUser(input: TerminalUserInput): Promise<TerminalUser> {
  return fetchJSON<TerminalUser>('/api/admin/terminal-users', {
    method: 'POST',
    headers: csrfHeaders(),
    body: JSON.stringify(input),
  });
}

export function deleteTerminalUser(username: string): Promise<void> {
  return fetchJSON<void>(`/api/admin/terminal-users/${encodeURIComponent(username)}`, {
    method: 'DELETE',
    headers: csrfHeaders(),
  });
}

/**
 * Sets the pacebi-managed web-terminal login password for a registry user —
 * separate from upsertTerminalUser's row fields and from the operator's
 * Pacesetter/ERP login at the green screen. 404s if `username` isn't a
 * registry row; 400s if `password` is under 8 chars (also enforced
 * client-side before this is called — see TerminalUsers.tsx's dialog).
 */
export function setTerminalUserPassword(username: string, password: string): Promise<void> {
  return fetchJSON<void>(`/api/admin/terminal-users/${encodeURIComponent(username)}/web-password`, {
    method: 'POST',
    headers: csrfHeaders(),
    body: JSON.stringify({ password }),
  });
}

/** The configured pr-ctrl printer catalog, for populating the 11 routing
 *  dropdowns in the provisioning dialog. Available even when writes are
 *  disabled (it's a read). */
export function getPrinters(): Promise<Printer[]> {
  return fetchJSON<Printer[]>('/api/printers');
}

/** The current startup.p row for `startdev` (a row's pacetty), or
 *  `found:false` if none exists yet. Available even when writes are
 *  disabled (it's a read). */
export function getTerminalStartup(startdev: string): Promise<TerminalStartup> {
  return fetchJSON<TerminalStartup>(`/api/terminal-startup/${encodeURIComponent(startdev)}`);
}

/**
 * Creates/updates a startup.p row via paceWriteServ's create-terminal verb.
 * A 400/409 means the daemon responded but declined (e.g. an invalid
 * printer number, or a duplicate startdev) — its message is surfaced
 * verbatim as the thrown Error's message (see terminalDeclineStatus in
 * handlers_terminal.go). 404 means writes_enabled is off; 403 means the
 * caller lacks the terminal:write permission.
 *
 * This intentionally doesn't reuse fetchJSON<void>() for the success path:
 * the 200 response is the daemon's raw create-terminal reply passed through
 * verbatim by the server, and its shape isn't part of this contract (the
 * caller only needs to know the write succeeded) — so we don't parse it,
 * which also avoids choking if that body is ever empty.
 */
export async function provisionTerminal(input: ProvisionTerminalInput): Promise<void> {
  const res = await fetch('/api/terminal-provision', {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify(input),
  });
  if (res.status === 401) throw new Error('unauthenticated');
  if (res.status === 403) throw new Error('forbidden');
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `HTTP ${res.status}`);
  }
}

/**
 * Downloads the root-run OS-login provisioning script for the whole
 * registry (useradd + install /icas/logins/<OSUser>.sh per user, cloned
 * from an on-box exemplar with each user's PACETTY substituted in).
 * pacebi only GENERATES this script — it's never executed here; the admin
 * reviews it and runs it as root. Same fetch→blob→temporary-<a> download
 * pattern as downloadInvoiceFile in api/invoiceFile.ts, just with a fixed
 * filename (the server always names it pace-provision-logins.sh) and plain
 * passthrough error text: a 409 means no exemplar login-script template
 * could be found, 403 means the caller lacks PermManageTerminalUsers, 503
 * means the registry store is unavailable — all surfaced verbatim from the
 * server's plain-text error body.
 */
export async function downloadProvisionScript(): Promise<void> {
  const res = await fetch('/api/admin/terminal-users/provision-script', { credentials: 'include' });
  if (!res.ok) throw new Error(await res.text());

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'pace-provision-logins.sh';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Creates an ERP green-screen operator login — a Progress _User
 * authenticated by SETUSERID, the account typed at the Pacesetter login
 * prompt. This is the MOST sensitive write exposed by this module: it mints
 * a brand-new auth account (server-audited without the password — see
 * handlers_paceuser.go's operator route). Entirely separate from
 * setTerminalUserPassword (the pacebi-managed WEB login used to sign into
 * this SPA's Pacesetter web terminal) and from the registry row itself.
 *
 * 400 = empty userid, or password under 8 chars (also enforced client-side
 * first — see TerminalUsers.tsx's operator dialog) — or another daemon
 * validation decline; 403 = caller lacks the owner-only operator:provision
 * permission; 404 = writes_enabled is off; 409 = an operator with that
 * userid already exists. All surfaced verbatim from the server's
 * plain-text error body.
 *
 * Same posture as provisionTerminal: doesn't force-parse the success body,
 * so the caller only needs to know the write went through.
 */
export async function createOperator(input: CreateOperatorInput): Promise<void> {
  const res = await fetch('/api/admin/terminal-users/operator', {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify(input),
  });
  if (res.status === 401) throw new Error('unauthenticated');
  if (res.status === 403) throw new Error('forbidden');
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `HTTP ${res.status}`);
  }
}
